import React from "react";
import PropTypes from "prop-types";
import * as Immutable from "immutable";
import ImmutablePropTypes from "react-immutable-proptypes";
import {
  getAvailableLanguages,
  getClientTranslatedLanguages
} from "selectors/languages";
import { connect } from "react-redux";
import { Client, Language } from "resourceModels";
import { getResource, getResources } from "selectors/resources";
import { NO_OP_FUNCTION } from "services/helpers";
import MultilingualInputLanguageButton from "./MultilingualInputLanguageButton";

import "./style.scss";

class MultilingualInputLanguages extends React.Component {
  onLanguageSelectBound = this.onLanguageSelect.bind(this);

  /**
   * Handlers
   */

  /**
   * @param {Language} language
   */
  onLanguageSelect(language) {
    const { setActiveLanguage } = this.props;
    setActiveLanguage(language.code);
  }

  /**
   * Properties
   */

  /**
   * @returns {Immutable.List.<Language>}
   */
  get messageLanguages() {
    const { message, languages } = this.props;
    const [...languageCodes] = message.keys();

    return Immutable.List(languageCodes.map(code => languages.find(l => l.code === code)));
  }

  /**
   * @returns {Immutable.List.<Language>}
   */
  get enabledLanguages() {
    const { languages, client } = this.props;
    let { messageLanguages: enabledLanguages } = this;
    const clientLanguage = languages.find(l => l.code === client.language);

    if (!enabledLanguages.includes(clientLanguage)) {
      enabledLanguages = enabledLanguages.unshift(clientLanguage);
    }

    // Put clientLanguage first
    return enabledLanguages.sort(language => (language === clientLanguage ? -1 : 0));
  }

  /**
   * @returns {Array.<Language>}
   */
  get availableLanguages() {
    const { clientTranslatedLanguages } = this.props;

    return clientTranslatedLanguages.filter(language => !this.messageLanguages.includes(language));
  }

  /**
   * Methods
   */

  /**
   * @returns {ReactElement}
   */
  render() {
    const {
      client,
      activeLanguage,
      invalidFields,
      languages,
      addLanguage,
      deleteLanguage
    } = this.props;

    return (
      <div className="MultilingualInputLanguages">
        <MultilingualInputLanguageButton
          availableLanguages={this.availableLanguages}
          onClick={this.onLanguageSelectBound}
          addLanguageAction={addLanguage}
          activeLanguage={activeLanguage}
          enabledLanguages={this.enabledLanguages}
          invalidFields={invalidFields}
          deleteLanguage={deleteLanguage}
          languages={languages}
          client={client}
        />
      </div>
    );
  }
}

MultilingualInputLanguages.propTypes = {
  client: PropTypes.instanceOf(Client).isRequired,
  activeLanguage: PropTypes.string.isRequired,
  invalidFields: ImmutablePropTypes.list,
  message: PropTypes.instanceOf(Immutable.Map).isRequired,
  clientTranslatedLanguages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  languages: ImmutablePropTypes.listOf(PropTypes.instanceOf(Language)).isRequired,
  addLanguage: PropTypes.func,
  deleteLanguage: PropTypes.func.isRequired,
  setActiveLanguage: PropTypes.func.isRequired
};

MultilingualInputLanguages.defaultProps = {
  invalidFields: Immutable.List(),
  addLanguage: NO_OP_FUNCTION
};

/**
 * @param {Object} state
 * @param {{message: Immutable.Map}} ownProps
 * @returns {{clientTranslatedLanguages: *, languages: *, availableLanguages: *, client: *}}
 */
function mapState(state, ownProps) {
  return {
    languages: getResources(state, "language"),
    client: getResource(state, "client"),
    clientTranslatedLanguages: getClientTranslatedLanguages(state),
    availableLanguages: getAvailableLanguages(state, ownProps)
  };
}

const Connector = connect(mapState, null)(MultilingualInputLanguages);

Connector.propTypes = {
  activeLanguage: PropTypes.string.isRequired,
  message: ImmutablePropTypes.mapOf(PropTypes.string, PropTypes.string).isRequired,
  addLanguage: PropTypes.func,
  deleteLanguage: PropTypes.func.isRequired,
  setActiveLanguage: PropTypes.func.isRequired
};

export default Connector;
